/* ============================================================
   MCC R1 (10/2026) · R1.4 one-tap "Add to cart" on product cards
   mcc-quick-add.js. Loaded once per page (defer) by snippets/mcc-header.liquid.
   Markup: snippets/mcc-card-add.liquid (rendered by snippets/card-product.liquid).
   Styles: assets/mcc-quick-add.css.

   - One delegated submit handler for form[data-mcc-add], in the capture phase (see the note at
     the listener: Shopify's own add-to-cart reporting must see each tap once). It also covers
     cards that arrive later: facets.js grid re-renders, related products injected by
     product-recommendations (global.js), and the phone "Load more".
   - It sends the request Dawn's product-form sends (assets/product-form.js): POST
     routes.cart_add_url with the form's FormData, Accept: application/javascript and
     X-Requested-With: XMLHttpRequest. Two differences: no `sections` parameter, and it never
     calls cart.renderContents(). So no cart notification or drawer opens.
   - After a successful add it GETs cart.js, writes item_count to #mcc-cart-count, publishes
     PUB_SUB_EVENTS.cartUpdate (assets/mcc-header.js subscribes as well; skipped on a product page
     when it would reset the main product's quantity box, see shouldPublishCartUpdate), pushes the
     dataLayer event 'mcc_card_add', and announces the result through a polite live region.
     'mcc_card_add' has its own name on purpose. Shopify's own add-to-cart reporting still fires
     on the AJAX add, so a GA4 add_to_cart is not counted twice.
   - Labels: "Added ✓" after success. It stays, and tapping again adds one more.
     HTTP 422 shows "Can't add more". Anything else shows "Try again", but only after cart.js
     confirms the add did not go through (see onFailed). When the page comes back from the
     back/forward cache, the labels are checked against cart.js (see the pageshow handler).
   - The add is never aborted on the client (fix round 3): like Dawn's product-form, the card keeps
     its spinner until Shopify answers.
   - If fetch, FormData or Promise is missing, this file does nothing. The form then posts
     normally and Shopify opens /cart.
   ============================================================ */
(function () {
  'use strict';

  if (window.mccQuickAddReady) return; // guard against a second include
  window.mccQuickAddReady = true;
  if (!window.fetch || !window.FormData || !window.Promise) return;

  var TEXT = {
    added: 'Added', // the tick (✓) is a separate aria-hidden span, shown by .is-added
    full: "Can't add more", // 422: the cart already holds all the stock there is (or a quantity limit)
    retry: 'Try again' // the add failed and cart.js shows it did not go through
  };
  // Fix round 3 (QA2-QA-2): the POST to /cart/add has no client timeout any more. Aborting it never
  // cancelled the add on Shopify's side, so a slow add that landed showed "Try again" and a retry
  // added a second unit. After QUEUE_HOLD_MS a slow add only stops holding up other cards' adds;
  // its own card keeps the spinner until Shopify answers.
  var QUEUE_HOLD_MS = 15000;
  var CART_TIMEOUT_MS = 10000;
  var SNAPSHOT_TIMEOUT_MS = 4000; // the cart read before a card's first add; on failure the add goes ahead

  var queue = Promise.resolve(); // one cart write at a time, so fast taps on several cards all land
  var liveRegion = null;
  var stateSeq = 0; // bumps on every label change, so a slow Back/Forward check never undoes a newer tap
  // Fix round 3 (QA2-QA-2): the last full cart this page read, used as the "before" quantity of an add.
  // cartEpoch bumps whenever the cart is about to change, so a cart.js reply that was requested before
  // that change is never kept as the snapshot.
  var lastCart = null;
  var cartEpoch = 0;

  /* ── Helpers ── */
  function addUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add';
  }

  function cartJsUrl() {
    return ((window.routes && window.routes.cart_url) || '/cart') + '.js';
  }

  // Used for cart.js reads only. A read is safe to abort; the add itself never is (see postAdd).
  function fetchWithTimeout(url, options, ms) {
    if (!window.AbortController) return fetch(url, options);
    var controller = new AbortController();
    var timer = window.setTimeout(function () {
      controller.abort();
    }, ms);
    options.signal = controller.signal;
    return fetch(url, options).then(
      function (response) {
        window.clearTimeout(timer);
        return response;
      },
      function (error) {
        window.clearTimeout(timer);
        throw error;
      }
    );
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function getCart(ms) {
    var epoch = cartEpoch;
    return fetchWithTimeout(
      cartJsUrl(),
      { credentials: 'same-origin', headers: { Accept: 'application/json' } },
      ms || CART_TIMEOUT_MS
    )
      .then(function (response) {
        if (!response.ok) throw new Error('cart.js ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        if (epoch === cartEpoch && cart && cart.items) lastCart = cart;
        return cart;
      });
  }

  // The cart is about to change (an add starts, or another part of the page changed it).
  function cartWillChange() {
    cartEpoch++;
    lastCart = null;
  }

  // How many units of this variant the cart holds (all lines).
  function qtyInCart(cart, variantId) {
    var n = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id || items[i].id) === String(variantId)) n += Number(items[i].quantity) || 0;
    }
    return n;
  }

  function setCount(count) {
    var badge = document.getElementById('mcc-cart-count'); // snippets/mcc-header.liquid
    if (!badge || typeof count !== 'number') return;
    badge.textContent = count > 0 ? String(count) : '';
    badge.setAttribute('data-count', String(count));
  }

  function refreshCount() {
    getCart().then(
      function (cart) {
        setCount(cart.item_count);
      },
      function () {}
    );
  }

  function publishSafe(eventKey, data) {
    // pubsub.js and constants.js are classic scripts: `publish` is a global function and
    // PUB_SUB_EVENTS a global const (so typeof, not window.PUB_SUB_EVENTS).
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    try {
      var result = publish(PUB_SUB_EVENTS[eventKey], data);
      if (result && typeof result.catch === 'function') result.catch(function () {});
    } catch (e) {
      /* a subscriber failing must not undo a successful add */
    }
  }

  // Fix round 3 (QA2-QA-1). On a product page, Dawn's product-info subscribes to cartUpdate when it has
  // a quantity box (product-info.js initQuantityHandlers). Every cartUpdate makes it re-read the main
  // product's quantity rules, and that resets the quantity box to its minimum (setQuantityBoundries).
  // That is right after the main product's own Add, but a "You may also like" card adding a different
  // product must not wipe a quantity the shopper already chose. So the publish is skipped when such a
  // product-info is on the page and none of them shows the variant the card added. Nothing else on a
  // product page listens: the header badge is set directly (setCount), and the cart page's cart-items
  // never sits next to a product-info. Returns true when the publish should go out as before.
  function shouldPublishCartUpdate(variantId) {
    var infos = document.querySelectorAll('product-info');
    var resets = false;
    for (var i = 0; i < infos.length; i++) {
      var info = infos[i];
      // Same test as Dawn's initQuantityHandlers: a quantity input and a .product-form__quantity block,
      // and not a quick-add modal copy (data-original-section).
      if (!info.querySelector('.quantity__input') || !info.querySelector('.product-form__quantity')) continue;
      if (info.hasAttribute('data-original-section')) continue;
      var idInput = info.querySelector('product-form [name="id"]');
      if (idInput && String(idInput.value) === String(variantId)) return true; // its own cart quantity changed
      resets = true;
    }
    return !resets;
  }

  function announce(message) {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(function () {
      liveRegion.textContent = message;
    }, 100);
  }

  function setBusy(button, busy) {
    var spinner = button.querySelector('.loading__spinner');
    if (busy) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  // state: 'added' | 'full' | 'retry' | 'default' (back to the button's own "Add to cart" label).
  function setState(button, state) {
    var label = button.querySelector('.mcc-add__label');
    if (label && !label.hasAttribute('data-mcc-default')) {
      label.setAttribute('data-mcc-default', label.textContent); // the locale's "Add to cart", kept for a reset
    }
    button.classList.toggle('is-added', state === 'added');
    button.classList.toggle('is-error', state === 'full' || state === 'retry');
    if (label) {
      if (TEXT[state]) label.textContent = TEXT[state];
      else if (state === 'default') label.textContent = label.getAttribute('data-mcc-default') || label.textContent;
    }
    button.setAttribute('data-mcc-seq', String(++stateSeq));
  }

  // The dataLayer list type. Cards in the PDP "You may also like" block arrive from
  // /recommendations/products, where Liquid's template.name is blank, so they are named here.
  function listType(form, button) {
    if (form.closest && form.closest('product-recommendations')) return 'related';
    return button.getAttribute('data-list') || '';
  }

  function track(info, cart) {
    try {
      var payload = {
        event: 'mcc_card_add',
        mcc_product_id: info.productId,
        mcc_variant_id: info.variantId,
        mcc_product_title: info.title,
        mcc_price: info.price,
        mcc_currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
        mcc_quantity: 1,
        mcc_list: window.location.pathname,
        mcc_list_type: info.list
      };
      if (cart && typeof cart.item_count === 'number') payload.mcc_cart_count = cart.item_count;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {
      /* analytics must never break the button */
    }
  }

  /* ── The add ── */
  // No client timeout and no AbortController (fix round 3, QA2-QA-2), as in Dawn's product-form.
  function postAdd(body) {
    return fetch(addUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    }).then(function (response) {
      return response
        .json()
        .then(null, function () {
          return null;
        })
        .then(function (data) {
          // Success = 2xx with the line item JSON. Errors come back as {status, message, description}.
          if (response.ok && data && !data.status) return data;
          var error = new Error((data && (data.description || data.message)) || 'Cart error');
          error.status = response.ok ? data && data.status : response.status;
          error.data = data;
          throw error;
        });
    });
  }

  // The variant's quantity before the add: from the last cart this page read when nothing has changed
  // the cart since, otherwise from a fresh cart.js read. null when it can't be read (the add still goes ahead).
  function snapshot(variantId) {
    if (lastCart) return Promise.resolve(qtyInCart(lastCart, variantId));
    return getCart(SNAPSHOT_TIMEOUT_MS).then(
      function (cart) {
        return qtyInCart(cart, variantId);
      },
      function () {
        return null;
      }
    );
  }

  function onAdded(button, info, cart) {
    setState(button, 'added');
    if (cart) setCount(cart.item_count);
    // Same payload shape as Dawn's publishers. When cart.js failed, cartData is null and
    // mcc-header.js re-reads the count itself.
    if (shouldPublishCartUpdate(info.variantId)) {
      publishSafe('cartUpdate', { source: 'mcc-quick-add', productVariantId: info.variantId, cartData: cart || null });
    } else if (!cart) {
      refreshCount(); // no publish, so mcc-header.js won't re-read the count: do it here
    }
    track(info, cart);
    var message = info.title + ' added to cart.';
    if (cart && typeof cart.item_count === 'number') {
      message += ' ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items') + ' in your cart.';
    }
    announce(message);
  }

  function showError(button, info, error, full) {
    setState(button, full ? 'full' : 'retry');
    if (full) {
      announce((error && error.data && error.data.description) || "Can't add more " + info.title + ' to your cart.');
    } else {
      announce((window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.');
    }
    if (error && error.data) {
      publishSafe('cartError', {
        source: 'mcc-quick-add',
        productVariantId: info.variantId,
        errors: error.data.errors || error.data.description,
        message: error.data.message
      });
    }
  }

  // Fix round 3 (QA2-QA-2). A 422 is Shopify refusing the add: "Can't add more". Any other failure (a
  // dropped connection, a gateway 5xx) can hide an add that went through, so cart.js decides: when the
  // variant's quantity is above the snapshot taken before the POST, the add landed and the card shows
  // "Added ✓" like any other success. "Try again" shows only when it did not land, or when the cart
  // can't be read to tell.
  function onFailed(button, info, error, before) {
    var status = error && error.status;
    if (status === 422 || status === '422') {
      showError(button, info, error, true);
      return;
    }
    return getCart().then(
      function (cart) {
        if (before !== null && qtyInCart(cart, info.variantId) > before) {
          onAdded(button, info, cart);
          return;
        }
        setCount(cart.item_count);
        showError(button, info, error, false);
      },
      function () {
        showError(button, info, error, false);
      }
    );
  }

  // Returns the promise the queue waits for: the whole add, or QUEUE_HOLD_MS after the POST went out,
  // whichever comes first. The card itself stays busy until the add has finished.
  function run(button, body, info) {
    function done() {
      setBusy(button, false); // tap again = one more
    }
    return snapshot(info.variantId).then(function (before) {
      cartWillChange();
      var finished = postAdd(body)
        .then(
          function () {
            // The add worked. The cart total only feeds the badge and the announcement.
            return getCart()
              .then(null, function () {
                return null;
              })
              .then(function (cart) {
                onAdded(button, info, cart);
              });
          },
          function (error) {
            return onFailed(button, info, error, before);
          }
        )
        .then(done, done);
      return Promise.race([finished, delay(QUEUE_HOLD_MS)]);
    });
  }

  /* ── Wiring ── */
  // The script is deferred, so <body> exists. role=status is polite and atomic.
  liveRegion = document.createElement('div');
  liveRegion.id = 'mcc-quick-add-status';
  liveRegion.className = 'visually-hidden';
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');
  document.body.appendChild(liveRegion);

  // Other cart changes on this page (the product page's own Add, the cart page) make the snapshot stale.
  // A full cart that comes with the event (the cart page) is kept as the new snapshot.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    try {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'mcc-quick-add') return;
        cartWillChange();
        if (event && event.cartData && event.cartData.items) lastCart = event.cartData;
      });
    } catch (e) {
      /* the snapshot is then simply re-read before each add */
    }
  }

  // Capture phase, on purpose (fix round 1, CMP-08). Shopify's web-pixels manager (WPM) reports
  // product_added_to_cart, which feeds the GA4 add_to_cart and Meta AddToCart app pixels, in two ways:
  //   1. its patched window.fetch, after a successful response from /cart/add (the request below), and
  //   2. a 'submit' listener it attaches at window load to every form whose action contains /cart/add.
  //      That listener does nothing when the event is already defaultPrevented.
  // Dawn's product-form calls preventDefault() in a listener on the form itself, so only path 1 reports
  // its adds. A bubbling listener on document would run AFTER WPM's form listener: every tap on a card
  // that was in the page at load would be reported twice. Capturing at document runs first, so
  // preventDefault() is already set when WPM's listener runs, and each tap is reported once, by the
  // fetch, exactly like Dawn's button.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || typeof form.hasAttribute !== 'function' || !form.hasAttribute('data-mcc-add')) return;
    if (event.defaultPrevented) return; // something else already took this submit
    var button = form.querySelector('.mcc-add__btn');
    if (!button) return; // unexpected markup: let the normal post happen
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return; // ignore taps while this card is busy

    var body = new FormData(form);
    var price = parseInt(button.getAttribute('data-price'), 10);
    var info = {
      variantId: String(body.get('id') || ''),
      productId: button.getAttribute('data-product-id') || '',
      title: button.getAttribute('data-title') || 'Item',
      price: isNaN(price) ? undefined : price / 100,
      list: listType(form, button)
    };

    setBusy(button, true); // spinner straight away, even while waiting in the queue
    queue = queue
      .then(function () {
        return run(button, body, info);
      })
      .then(null, function () {
        // Belt and braces: never leave the queue rejected (later taps would never run) or this card spinning.
        setBusy(button, false);
      });
  }, true);

  // Back/forward cache restore (e.g. Add, open /cart, remove the item, press Back). The page comes back
  // exactly as it was left, so (mcc-header.js re-reads the header count on the same event):
  // - a card frozen mid-request loses its spinner;
  // - "Added ✓" stays only on cards whose variant is still in the cart; the rest go back to "Add to cart";
  // - "Can't add more" / "Try again" are stale after leaving the page, so they go back to "Add to cart".
  // If cart.js can't be read, only the error labels are reset ("Added ✓" may still be true).
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    cartWillChange(); // the cart may have changed while the page was away; the read below refreshes it
    var busy = document.querySelectorAll('.mcc-add__btn.loading');
    for (var i = 0; i < busy.length; i++) setBusy(busy[i], false);

    var marked = document.querySelectorAll('.mcc-add__btn.is-added, .mcc-add__btn.is-error');
    if (!marked.length) return;
    var seen = [];
    for (var j = 0; j < marked.length; j++) seen.push(marked[j].getAttribute('data-mcc-seq'));

    function reset(keepAdded) {
      for (var k = 0; k < marked.length; k++) {
        var button = marked[k];
        // Skip a card the shopper tapped again while cart.js was loading (busy, or a newer label).
        if (button.getAttribute('aria-disabled') === 'true' || button.getAttribute('data-mcc-seq') !== seen[k]) continue;
        if (button.classList.contains('is-added') && keepAdded(button)) continue;
        setState(button, 'default');
      }
    }

    getCart().then(
      function (cart) {
        var inCart = {};
        var items = (cart && cart.items) || [];
        for (var n = 0; n < items.length; n++) inCart[String(items[n].variant_id || items[n].id)] = true;
        reset(function (button) {
          var idInput = button.form && button.form.querySelector('input[name="id"]');
          return !!(idInput && inCart[String(idInput.value)]);
        });
      },
      function () {
        reset(function () {
          return true;
        });
      }
    );
  });
})();
